import { useCallback, useEffect, useState } from "react";

import {
  createProfile as dbCreateProfile,
  deleteProfile as dbDeleteProfile,
  listProfiles,
  updateProfile as dbUpdateProfile,
  type ProfileInput,
} from "@/db/profiles";
import type { CatalogProfile } from "@/types/schemas";

/** localStorage key holding the active profile id (session continuity). */
export const ACTIVE_PROFILE_KEY = "catalogkit.activeProfileId";

function readActiveId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_PROFILE_KEY);
  } catch {
    return null;
  }
}

function writeActiveId(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(ACTIVE_PROFILE_KEY);
    else localStorage.setItem(ACTIVE_PROFILE_KEY, id);
  } catch {
    // Storage unavailable (private mode) — session continuity degrades gracefully.
  }
}

export interface UseProfilesResult {
  profiles: CatalogProfile[];
  activeProfile: CatalogProfile | null;
  setActiveProfile: (id: string | null) => void;
  createProfile: (input: ProfileInput) => Promise<CatalogProfile>;
  updateProfile: (
    id: string,
    patch: Partial<Omit<CatalogProfile, "id">>,
  ) => Promise<CatalogProfile>;
  deleteProfile: (id: string, options?: { cascade?: boolean }) => Promise<void>;
  refresh: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Manage catalog profiles. Loads all profiles from IndexedDB, tracks the active
 * profile (persisted to localStorage for session continuity), and exposes
 * audit-logged create/update/delete wrappers that refresh local state.
 */
export function useProfiles(): UseProfilesResult {
  const [profiles, setProfiles] = useState<CatalogProfile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(readActiveId);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      setProfiles(await listProfiles());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setActiveProfile = useCallback((id: string | null): void => {
    setActiveId(id);
    writeActiveId(id);
  }, []);

  const createProfile = useCallback(
    async (input: ProfileInput): Promise<CatalogProfile> => {
      const profile = await dbCreateProfile(input);
      await refresh();
      return profile;
    },
    [refresh],
  );

  const updateProfile = useCallback(
    async (
      id: string,
      patch: Partial<Omit<CatalogProfile, "id">>,
    ): Promise<CatalogProfile> => {
      const profile = await dbUpdateProfile(id, patch);
      await refresh();
      return profile;
    },
    [refresh],
  );

  const deleteProfile = useCallback(
    async (id: string, options?: { cascade?: boolean }): Promise<void> => {
      await dbDeleteProfile(id, { cascade: options?.cascade ?? false });
      if (id === activeId) setActiveProfile(null);
      await refresh();
    },
    [refresh, activeId, setActiveProfile],
  );

  const activeProfile = profiles.find((p) => p.id === activeId) ?? null;

  return {
    profiles,
    activeProfile,
    setActiveProfile,
    createProfile,
    updateProfile,
    deleteProfile,
    refresh,
    isLoading,
    error,
  };
}
